import { memo, useCallback, useEffect, useState, type DragEvent as ReactDragEvent } from 'react';
import { ActivityIndicator, ScrollView, Text, View } from 'react-native';
import { usePathname, useRouter } from 'expo-router';

import { ChatConversationList } from '@/components/chat/ChatConversationList.web';
import { SearchNavQueryBar, SearchNavResults } from '@/components/search/SearchNavPanel.web';
import { createBlock, fetchBlock, fetchBlockTree, prefetchPageDocument } from '@/lib/blocks/api';
import { newBlockId } from '@/lib/blocks/ids';
import {
  duplicatePage,
  promptRename,
  renamePage,
  trashPage,
} from '@/lib/blocks/page-actions';
import type { BlockRecord } from '@/lib/blocks/types';
import { useStoragePersistence } from '@/lib/db/persistence';
import { loadFavorites } from '@/lib/favorites/favorites-api';
import { loadLlmPrivate } from '@/lib/llm-private/llm-private-api';
import type { FavoritePage } from '@/lib/favorites/types';
import { isMissingBlockError } from '@/lib/net/api-error';
import { removeRecentPage } from '@/lib/navigation/recent-pages';
import { findSidebarParentId } from '@/lib/layout/sidebar-nav-index';
import {
  applyOptimisticSidebarMove,
  isDescendantInTree,
  moveSidebarBlock,
  sortByPosition,
  type SidebarDropTarget,
} from '@/lib/layout/sidebar-tree-move';
import {
  pageHasChildPagesInTree,
  prefetchSidebarPageChildLists,
} from '@/lib/layout/sidebar-child-pages';

import { ConnectionBanner } from './ConnectionBanner';
import { SidebarItem } from './SidebarItem';
import { SidebarFooter } from './SidebarFooter';
import { SidebarEmptyHint } from './SidebarEmptyHint';
import { SidebarNavSections } from './SidebarNavSections';
import { SidebarTabBar } from './SidebarTabBar.web';
import { useSidebar } from './SidebarContext';
import { activeChatIdFromPath, sidebarTabFromPath } from '@/lib/layout/sidebar-tab';

type SidebarProps = {
  activePageId: string | null;
  onNavigate?: () => void;
  onCollapse?: () => void;
};

type DropHint = 'before' | 'inside';

type DropState = {
  blockId: string;
  hint: DropHint;
} | null;

const NEST_FRACTION = 0.45;

function tailDropId(parentId: string): string {
  return `__tail_${parentId}`;
}

function dropHintFromEvent(event: ReactDragEvent<HTMLElement>, canNest: boolean): DropHint {
  const row = event.currentTarget;
  const rect = row.getBoundingClientRect();
  const rel = (event.clientY - rect.top) / Math.max(rect.height, 1);
  if (!canNest || rel < NEST_FRACTION) {
    return 'before';
  }
  return 'inside';
}

export const Sidebar = memo(function Sidebar({
  activePageId,
  onNavigate,
  onCollapse,
}: SidebarProps) {
  const router = useRouter();
  const pathname = usePathname();
  const sidebarTab = sidebarTabFromPath(pathname);
  const activeChatId = activeChatIdFromPath(pathname);
  const {
    navSections,
    recentPages,
    favoritePages,
    loading,
    error,
    childrenByParent,
    ensureChildren,
    addChild,
    removeChild,
    updateChild,
    setChildren,
    invalidateChildren,
    expanded,
    toggleExpanded,
    expandToPage,
  } = useSidebar();
  const storagePersistence = useStoragePersistence();
  const [dragBlockId, setDragBlockId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<DropState>(null);

  useEffect(() => {
    if (activePageId) {
      void expandToPage(activePageId);
    }
  }, [activePageId, expandToPage]);

  const clearDrag = useCallback(() => {
    setDragBlockId(null);
    setDropTarget(null);
  }, []);

  const commitDrop = useCallback(
    async (target: SidebarDropTarget, expandParentId?: string) => {
      if (!dragBlockId) {
        return;
      }

      const newParentId = target.kind === 'inside' ? target.parentId : target.parentId;

      if (
        dragBlockId === newParentId ||
        isDescendantInTree(newParentId, dragBlockId, childrenByParent)
      ) {
        clearDrag();
        return;
      }

      let oldParentId = '';
      try {
        const optimistic = applyOptimisticSidebarMove(childrenByParent, dragBlockId, target);
        oldParentId = optimistic.oldParentId;
        setChildren(optimistic.oldParentId, optimistic.next[optimistic.oldParentId] ?? []);
        setChildren(optimistic.newParentId, optimistic.next[optimistic.newParentId] ?? []);

        const result = await moveSidebarBlock(dragBlockId, target, optimistic.next);
        setChildren(result.newParentId, result.siblings);
        if (result.oldParentId !== result.newParentId) {
          const oldSiblings = sortByPosition(optimistic.next[result.oldParentId] ?? []);
          setChildren(result.oldParentId, oldSiblings);
        }

        if (expandParentId && !expanded[expandParentId]) {
          toggleExpanded(expandParentId, false);
        }
      } catch {
        if (oldParentId) {
          invalidateChildren(oldParentId);
        }
        invalidateChildren(newParentId);
      } finally {
        clearDrag();
      }
    },
    [
      childrenByParent,
      dragBlockId,
      expanded,
      clearDrag,
      invalidateChildren,
      setChildren,
      toggleExpanded,
    ],
  );

  function openPage(id: string, type: string): void {
    onNavigate?.();
    if (type === 'database') {
      router.push(`/database/${id}`);
      return;
    }
    prefetchPageDocument(id);
    router.push(`/page/${id}`);
  }

  async function onNewSubpage(blockParentId: string): Promise<void> {
    const siblings = await fetchBlockTree(blockParentId);
    const pageSiblings = siblings.filter((b) => b.type === 'page' || b.type === 'database');
    const block = await createBlock({
      id: newBlockId(),
      parentId: blockParentId,
      type: 'page',
      title: 'Untitled',
      position: pageSiblings.length,
    });
    addChild(blockParentId, block);
    openPage(block.id, 'page');
  }

  async function onDuplicate(block: BlockRecord, parentId: string): Promise<void> {
    const copy = await duplicatePage(block.id, parentId);
    addChild(parentId, copy);
    openPage(copy.id, 'page');
  }

  async function onRename(block: BlockRecord, parentId: string, displayLabel: string): Promise<void> {
    const nextTitle = promptRename(block.title ?? displayLabel);
    if (nextTitle === null) {
      return;
    }
    const updated = await renamePage(block.id, nextTitle);
    updateChild(parentId, block.id, { title: updated.title });
  }

  async function onTrash(block: BlockRecord, parentId: string): Promise<void> {
    try {
      await trashPage(block.id, block);
    } catch (err) {
      if (!isMissingBlockError(err)) {
        console.error(err);
        return;
      }
    }
    invalidateChildren(block.id);
    removeRecentPage(block.id);
    removeChild(parentId, block.id);
    void loadFavorites();
    void loadLlmPrivate();
    if (
      activePageId === block.id ||
      (activePageId != null && isDescendantInTree(activePageId, block.id, childrenByParent))
    ) {
      router.replace(`/page/${parentId}`);
    }
  }

  async function onMenuTrash(pageId: string): Promise<void> {
    const parentId = findSidebarParentId(pageId, childrenByParent);
    const block = parentId
      ? (childrenByParent[parentId] ?? []).find((row) => row.id === pageId)
      : undefined;
    if (block && parentId) {
      await onTrash(block, parentId);
      return;
    }

    removeRecentPage(pageId);
    if (parentId) {
      invalidateChildren(pageId);
      removeChild(parentId, pageId);
    }
    void loadFavorites();
    void loadLlmPrivate();
  }

  async function resolveFavoriteBlock(page: FavoritePage) {
    try {
      return await fetchBlock(page.id);
    } catch {
      return null;
    }
  }

  async function onFavoriteDuplicate(page: FavoritePage): Promise<void> {
    const block = await resolveFavoriteBlock(page);
    if (!block?.parentId) {
      return;
    }
    await onDuplicate(block, block.parentId);
  }

  async function onFavoriteRename(page: FavoritePage): Promise<void> {
    const block = await resolveFavoriteBlock(page);
    if (!block?.parentId) {
      return;
    }
    await onRename(block, block.parentId, page.title);
  }

  async function onFavoriteTrash(page: FavoritePage): Promise<void> {
    await onMenuTrash(page.id);
  }

  return (
    <View className="flex h-full min-h-0 flex-1 flex-col bg-surface">
      <SidebarTabBar onCollapse={onCollapse} onNavigate={onNavigate} />

      <ConnectionBanner />

      {storagePersistence === 'memory' ? (
        <View className="border-b border-warning bg-warning-muted px-1.5 py-1">
          <Text className="text-[11px] font-semibold text-fg">
            Offline edits are not being saved
          </Text>
          <Text className="mt-0.5 text-[10px] leading-snug text-fg-muted">
            This browser session is using in-memory SQLite only, so changes are lost on reload.
            Edits still sync while you are online.
          </Text>
        </View>
      ) : null}

      {sidebarTab === 'search' ? (
        <View className="min-h-0 flex-1 flex-col">
          <SearchNavQueryBar />
          <ScrollView
            className="min-h-0 flex-1 px-0.5 py-0.5"
            contentContainerClassName="items-stretch"
            showsVerticalScrollIndicator
          >
            <SearchNavResults />
          </ScrollView>
        </View>
      ) : (
      <ScrollView
        className="min-h-0 flex-1 px-0.5 py-0.5"
        contentContainerClassName={
          sidebarTab === 'home' && loading ? 'items-center justify-center py-8' : 'pb-2'
        }
        showsVerticalScrollIndicator
      >
        {sidebarTab === 'agents' ? (
          <ChatConversationList
            activeConversationId={activeChatId}
            onNavigate={onNavigate}
            variant="sidebar"
          />
        ) : loading ? (
          <ActivityIndicator size="small" />
        ) : error ? (
          <Text className="px-1.5 text-xs text-danger">{error}</Text>
        ) : (
          <SidebarNavSections
            activePageId={activePageId}
            childrenByParent={childrenByParent}
            expanded={expanded}
            navSections={navSections}
            onEnsureChildren={(parentId) => void ensureChildren(parentId)}
            onNewSubpageAtRoot={(rootId) => void onNewSubpage(rootId)}
            onOpenPage={openPage}
            favoritePages={favoritePages}
            onFavoriteDuplicate={(page) => void onFavoriteDuplicate(page)}
            onFavoriteRename={(page) => void onFavoriteRename(page)}
            onFavoriteTrash={(page) => void onFavoriteTrash(page)}
            onRecentTrash={(page) => void onMenuTrash(page.id)}
            recentPages={recentPages}
            renderWorkspaceTree={(rootBlockId) => (
              <TreeNode
                activePageId={activePageId}
                commitDrop={commitDrop}
                depth={1}
                dragBlockId={dragBlockId}
                dropTarget={dropTarget}
                onClearDrag={clearDrag}
                onDuplicate={onDuplicate}
                onEnsureChildren={ensureChildren}
                onNavigate={openPage}
                onNewSubpage={onNewSubpage}
                onRename={onRename}
                onSetDragBlockId={setDragBlockId}
                onSetDropTarget={setDropTarget}
                onTrash={onTrash}
                parentId={rootBlockId}
              />
            )}
            toggleExpanded={toggleExpanded}
          />
        )}
      </ScrollView>
      )}

      <SidebarFooter />
    </View>
  );
});

function TreeNode({
  parentId,
  depth,
  activePageId,
  dragBlockId,
  dropTarget,
  onSetDragBlockId,
  onSetDropTarget,
  onClearDrag,
  commitDrop,
  onNavigate,
  onEnsureChildren,
  onNewSubpage,
  onDuplicate,
  onRename,
  onTrash,
}: {
  parentId: string;
  depth: number;
  activePageId: string | null;
  dragBlockId: string | null;
  dropTarget: DropState;
  onSetDragBlockId: (id: string | null) => void;
  onSetDropTarget: (state: DropState) => void;
  onClearDrag: () => void;
  commitDrop: (target: SidebarDropTarget, expandParentId?: string) => Promise<void>;
  onNavigate: (id: string, type: string) => void;
  onEnsureChildren: (parentId: string) => Promise<BlockRecord[]>;
  onNewSubpage: (blockParentId: string) => Promise<void>;
  onDuplicate: (block: BlockRecord, parentId: string) => Promise<void>;
  onRename: (block: BlockRecord, parentId: string, displayLabel: string) => Promise<void>;
  onTrash: (block: BlockRecord, parentId: string) => Promise<void>;
}) {
  const { childrenByParent, expanded, toggleExpanded } = useSidebar();
  const loaded = parentId in childrenByParent;
  const children = sortByPosition(childrenByParent[parentId] ?? []);
  const tailId = tailDropId(parentId);
  useEffect(() => {
    if (loaded) {
      return;
    }
    void onEnsureChildren(parentId);
  }, [loaded, parentId, onEnsureChildren]);

  useEffect(() => {
    prefetchSidebarPageChildLists(children, childrenByParent, onEnsureChildren);
  }, [children, childrenByParent, onEnsureChildren]);

  if (loaded && depth === 1 && children.length === 0) {
    return <SidebarEmptyHint message="Nothing here yet. Use + to add a page." />;
  }

  return (
    <View className="relative">
      {children.map((block, index) => {
        const isPage = block.type === 'page';
        const isOpen = expanded[block.id] ?? false;
        const label = block.title ?? (block.type === 'database' ? 'Database' : 'Untitled');
        const isActive = activePageId === block.id;
        const childList = childrenByParent[block.id];
        const hasChildPages = pageHasChildPagesInTree(block.id, block.type, childrenByParent);
        const canNest = block.type === 'page' || block.type === 'database';
        const isDragging = dragBlockId === block.id;
        const dropHint = dropTarget?.blockId === block.id ? dropTarget.hint : null;

        return (
          <View key={block.id}>
            <SidebarItem
              blockId={block.id}
              depth={depth}
              draggable
              dropHint={dropHint}
              hasChildPages={hasChildPages}
              isActive={isActive}
              isDragging={isDragging}
              isExpanded={isOpen}
              label={label}
              leadingIcon={block.type === 'database' ? 'database' : 'page'}
              onDragEnd={onClearDrag}
              onDragLeave={() => {
                if (dropTarget?.blockId === block.id) {
                  onSetDropTarget(null);
                }
              }}
              onDragOver={(event) => {
                if (!dragBlockId || dragBlockId === block.id) {
                  return;
                }
                const hint = dropHintFromEvent(event, canNest);
                onSetDropTarget({ blockId: block.id, hint });
              }}
              onDragStart={() => onSetDragBlockId(block.id)}
              onDrop={(event) => {
                if (!dragBlockId || dragBlockId === block.id) {
                  return;
                }
                const hint = dropHintFromEvent(event, canNest);
                if (hint === 'inside') {
                  void commitDrop({ kind: 'inside', parentId: block.id }, block.id);
                  return;
                }
                void commitDrop({ kind: 'before', parentId, index });
              }}
              onDuplicate={() => void onDuplicate(block, parentId)}
              onNewSubpage={isPage ? () => void onNewSubpage(block.id) : undefined}
              onPrefetch={isPage ? () => prefetchPageDocument(block.id) : undefined}
              onPrefetchChildren={
                isPage && childList === undefined
                  ? () => void onEnsureChildren(block.id)
                  : undefined
              }
              onPress={() => onNavigate(block.id, block.type)}
              onRename={() => void onRename(block, parentId, label)}
              onToggleExpand={hasChildPages ? () => toggleExpanded(block.id) : undefined}
              onTrash={() => void onTrash(block, parentId)}
            />
            {isPage && isOpen ? (
              <TreeNode
                activePageId={activePageId}
                commitDrop={commitDrop}
                depth={depth + 1}
                dragBlockId={dragBlockId}
                dropTarget={dropTarget}
                onClearDrag={onClearDrag}
                onDuplicate={onDuplicate}
                onEnsureChildren={onEnsureChildren}
                onNavigate={onNavigate}
                onNewSubpage={onNewSubpage}
                onRename={onRename}
                onSetDragBlockId={onSetDragBlockId}
                onSetDropTarget={onSetDropTarget}
                onTrash={onTrash}
                parentId={block.id}
              />
            ) : null}
          </View>
        );
      })}
      {dragBlockId ? (
        <ListTailDropZone
          depth={depth}
          dropActive={dropTarget?.blockId === tailId}
          onDragLeave={() => {
            if (dropTarget?.blockId === tailId) {
              onSetDropTarget(null);
            }
          }}
          onDragOver={() => onSetDropTarget({ blockId: tailId, hint: 'before' })}
          onDrop={() => void commitDrop({ kind: 'before', parentId, index: children.length })}
        />
      ) : null}
    </View>
  );
}

function ListTailDropZone({
  depth,
  dropActive,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  depth: number;
  dropActive: boolean;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: () => void;
}) {
  const paddingLeft = 4 + depth * 12;
  return (
    <div
      className="sidebar-tail-drop"
      style={{ paddingLeft }}
      onDragLeave={onDragLeave}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        onDragOver();
      }}
      onDrop={(event) => {
        event.preventDefault();
        onDrop();
      }}
    >
      {dropActive ? (
        <div className="sidebar-drop-indicator sidebar-drop-indicator-tail" />
      ) : null}
    </div>
  );
}
